/* Per-(source,event) presentation: turns a notification into an icon + message + in-app route */
import { ACCESS_MANAGEMENT_SOURCE, AccessRequestNotification, parseAccessRequestNotification, XptNotification } from './notificationModels';

export interface NotificationPresentation {
  iconName: string; // Cloudscape icon name
  message: string;
  route: string | null; // in-app SPA route, or null when the item is not click-through
}

/** `owns` decides whether this presenter handles the notification
 * `present` renders it (only called when `owns` returned true).
 */
interface NotificationPresenter {
  owns: (notification: XptNotification) => boolean;
  present: (notification: XptNotification) => NotificationPresentation;
}

/** Fallback for notifications no presenter owns, or whose payload failed to parse. We still
 * show the item but with a "failed to load" message and no route, so the user knows it's broken/
 * The console.error in the payload parser carries the real diagnostics. */
const genericPresentation = (): NotificationPresentation => ({
  iconName: 'status-warning',
  message: 'This notification could not be loaded',
  route: null
});

// ── Access-management presenter ──────────────────────────────────────────────────────────────────

const nameAlias = (name: string, alias: string): string => `${name} (${alias})`;

/** The effective outcome of a request, or null while still pending. Comes from the item's own `kind`
 * (requester/beneficiary standalone outcome items) OR from `resolution_action` on a reconciled-in-place
 * SUBMITTED item (the FAs' item, whose `kind` stays SUBMITTED). */
const resolutionOf = (req: AccessRequestNotification): 'APPROVED' | 'REJECTED' | 'CANCELLED' | null => {
  if (req.kind !== 'SUBMITTED') {
    return req.kind;
  }
  return req.resolution_action ?? null;
};

const accessRequestMessage = (req: AccessRequestNotification): string => {
  const requester = nameAlias(req.requester_name, req.requester_alias);
  const beneficiary = nameAlias(req.beneficiary_name, req.beneficiary_alias);
  const isSelf = req.requester_alias === req.beneficiary_alias;
  const role = req.requested_role;
  const bg = req.business_group_name;
  const resolution = resolutionOf(req);

  // Still pending (a SUBMITTED item with no resolution yet): describe who requested what.
  if (!resolution) {
    return isSelf ? `${requester} requested ${role} for ${bg}` : `${requester} requested ${role} for ${bg} on behalf of ${beneficiary}`;
  }

  // Resolved (approved/rejected/cancelled)
  const verb = { APPROVED: 'approved', REJECTED: 'rejected', CANCELLED: 'cancelled' }[resolution];
  const by = req.resolved_by ? ` by ${req.resolved_by}` : '';
  return `${beneficiary}'s ${role} request for ${bg} was ${verb}${by}`;
};

/** Icon reflecting the request's outcome: approved -> tick, rejected/cancelled -> cross, still
 * pending -> pending clock. */
const iconForResolution = (resolution: 'APPROVED' | 'REJECTED' | 'CANCELLED' | null): string => {
  switch (resolution) {
    case 'APPROVED':
      return 'status-positive';
    case 'REJECTED':
    case 'CANCELLED':
      return 'status-negative';
    default:
      return 'status-pending';
  }
};

const accessRequestPresenter: NotificationPresenter = {
  owns: (n) => n.source === ACCESS_MANAGEMENT_SOURCE,
  present: (n) => {
    const req = parseAccessRequestNotification(n);
    if (!req) {
      return genericPresentation();
    }
    // SUBMITTED item is the reviewer's "pending your review" notification -> the Finance Admin pending-requests console
    // APPROVED/REJECTED/CANCELLED are the requester/beneficiary's outcome -> their own requests page.
    const route = req.kind === 'SUBMITTED' ? '/admin-console/access-management' : '/my-requests';
    return {
      iconName: iconForResolution(resolutionOf(req)),
      message: accessRequestMessage(req),
      route
    };
  }
};

// ── Registry ─────────────────────────────────────────────────────────────────────────────────────

const PRESENTERS: NotificationPresenter[] = [accessRequestPresenter];

/** Build the presentation for a notification, delegating to the presenter that owns its type. */
export const getNotificationPresentation = (notification: XptNotification): NotificationPresentation => {
  const presenter = PRESENTERS.find((p) => p.owns(notification));
  return presenter ? presenter.present(notification) : genericPresentation();
};
